import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { User, Mail, Phone, Shield, Building2, FileText, Calendar, Clock } from "lucide-react";

const roleLabels: Record<string, string> = {
  support: "دعم فني", call_center: "مركز اتصال", accountant: "محاسب", developer: "مطوّر", supervisor: "مشرف",
};
const departmentLabels: Record<string, string> = {
  general: "عام", technical: "تقني", financial: "مالي", operations: "عمليات", customer_service: "خدمة العملاء",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  staff: any | null;
}

const SystemStaffDetailsDialog = ({ open, onOpenChange, staff }: Props) => {
  if (!staff) return null;

  const InfoRow = ({ icon: Icon, label, value, color }: { icon: any; label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex items-start gap-3 py-2">
      <div className="p-1.5 rounded-lg bg-muted"><Icon className={`w-4 h-4 ${color || "text-muted-foreground"}`} /></div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>تفاصيل الموظف</DialogTitle>
        </DialogHeader>
        <div className="space-y-1">
          {/* Header */}
          <div className="flex items-center gap-4 pb-2">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold">{staff.full_name}</h3>
              <Badge variant="secondary">{roleLabels[staff.role] || staff.role}</Badge>
              <Badge className={`mr-2 text-[10px] ${staff.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                {staff.status === "active" ? "نشط" : "معطّل"}
              </Badge>
            </div>
          </div>

          <Separator />

          <InfoRow icon={Mail} label="البريد الإلكتروني" value={staff.email} />
          <InfoRow icon={Phone} label="الهاتف" value={staff.phone || "غير محدد"} />
          <InfoRow icon={Shield} label="الدور" value={roleLabels[staff.role] || staff.role} color="text-primary" />
          <InfoRow icon={Building2} label="القسم" value={departmentLabels[staff.department || "general"] || staff.department} />

          {staff.notes && (
            <>
              <Separator />
              <InfoRow icon={FileText} label="ملاحظات" value={staff.notes} />
            </>
          )}

          <Separator />
          <InfoRow icon={Calendar} label="تاريخ الإضافة" value={format(new Date(staff.created_at), "dd MMMM yyyy", { locale: ar })} />
          <InfoRow icon={Clock} label="آخر تحديث" value={format(new Date(staff.updated_at), "dd MMMM yyyy HH:mm", { locale: ar })} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SystemStaffDetailsDialog;
